<mat-expansion-panel>
  <mat-expansion-panel-header>
    <mat-panel-title data-cy="payments-header">Payments</mat-panel-title>
  </mat-expansion-panel-header>

  @if (showStripeLink) {
    @if (!stripeIsActive) {
      <div class="stripe-message">
        Please sign up for a Stripe™ account to start receiving payments and viewing payment history.
        <div (click)="doStripeConnect()" class="fc-link">
          <fa-icon [icon]="['fas', 'arrow-up-right-from-square']" class="blue download-icon"></fa-icon>
          <span>Set Up A Stripe™ Payment Account</span>
        </div>
      </div>
    } @else {
      <div class="payment-info">
        <p>
          Payments will continue provided that reporting is completed on time, disturbances are accurately recorded, and
          the property remains in good standing. All payments are securely made through Stripe.
        </p>
        <p>You can use the link below to access your payment history.</p>
        <div (click)="getLoginLink()" class="fc-link">
          <fa-icon [icon]="['fas', 'arrow-up-right-from-square']" class="blue download-icon"></fa-icon>
          <span>Visit Stripe™ Account</span>
        </div>
      </div>
    }
  }

  <div class="section-content payment-history">
    <div class="title">Accrual Periods</div>
    <table [dataSource]="dataSource" class="fc-table" mat-table aria-describedby="List of Payment Periods">
      <ng-container matColumnDef="accrual">
        <th scope="col" *matHeaderCellDef mat-header-cell>Period</th>
        <td *matCellDef="let element" mat-cell>
          {{ (element.sequence == 0 ? site.getDate(site.submittedDate) : element.startDate) | date: 'shortDate' }}
          - {{ element.endDate | date: 'shortDate' }}
        </td>
      </ng-container>
      <ng-container matColumnDef="payment">
        <th scope="col" *matHeaderCellDef mat-header-cell>Payment Date</th>
        <td *matCellDef="let element" mat-cell>
          {{ (element.payment ? element.payment.createDate : getEstimatedPaymentDate(element)) | date: 'shortDate' }}
          @if (!element.payment) {
            <div class="description"><sup>*</sup>Estimated</div>
          }
        </td>
      </ng-container>
      <ng-container matColumnDef="report">
        <th scope="col" *matHeaderCellDef mat-header-cell>Periodic Report</th>
        <td *matCellDef="let element" mat-cell>
          @if (element.reportResponseId) {
            <a
              [routerLink]="[router.url, 'report-response', element.reportResponse.id]"
              rel="noopener noreferrer">
              {{ element.reportResponse.createDate | date: 'shortDate' }}
            </a>
          }

        </td>
      </ng-container>
      <ng-container matColumnDef="base">
        <th scope="col" *matHeaderCellDef mat-header-cell>Base ERT</th>
        <td *matCellDef="let element" mat-cell>
          {{ element.netErt - element.deductionErt | number: '1.0-4' }} ERT
        </td>
      </ng-container>
      <ng-container matColumnDef="adjustments">
        <th scope="col" *matHeaderCellDef mat-header-cell>ERT Deductions</th>
        <td *matCellDef="let element" mat-cell>
          @for (adjustment of element.adjustments; track adjustment) {
            <div class="payment-adjustment">
              {{ getAdjustmentViewName(adjustment.adjustmentType.shortName) }}
              {{ adjustment.value }} ERT
            </div>
          }
        </td>
      </ng-container>
      <ng-container matColumnDef="net">
        <th scope="col" *matHeaderCellDef mat-header-cell>Net ERT</th>
        <td *matCellDef="let element" mat-cell>
          {{ element.netErt }}
          @if (element.netErtAdjustment) {
            <div class="description">Adjustment: {{ element.netErtAdjustment }}</div>
          }
        </td>
      </ng-container>
      <ng-container matColumnDef="ppt">
        <th scope="col" *matHeaderCellDef mat-header-cell>PPT</th>
        <td *matCellDef="let element" mat-cell>
          @if (isFutureVariablePeriod(element)) {
            TBD
          } @else {
            {{ element.ppt | currency: 'USD' }}
          }
          @if (element.isVariablePeriod) {
            <span class="variable-icon">
              <fa-icon
                #tooltip1="matTooltip"
                (click)="tooltip1.toggle()"
                [icon]="['far', 'circle-info']"
                [matTooltip]="
              'Market Rate ERT: This price is subject to carbon offset market rates and will be determined shortly before reporting and payment.'
            "
                class="blue"
                matTooltipClass="fc-tooltip"
                matTooltipPosition="right"></fa-icon>
              Market Rate
            </span>
          }
        </td>
      </ng-container>
      <ng-container matColumnDef="payout_adjustment">
        <th scope="col" *matHeaderCellDef mat-header-cell>Payment Adjustments</th>
        <td *matCellDef="let element" mat-cell>
          {{ element.netPayoutAdjustment | currency: 'USD' }}
        </td>
      </ng-container>
      <ng-container matColumnDef="total">
        <th scope="col" *matHeaderCellDef mat-header-cell>Total</th>
        <td *matCellDef="let element" mat-cell>
          @if (isFutureVariablePeriod(element)) {
            TBD
          } @else {
            {{ element.netPayout | currency: 'USD' }}
          }
        </td>
      </ng-container>
      <ng-container matColumnDef="actions">
        <th scope="col" *matHeaderCellDef mat-header-cell>Actions</th>
        <td *matCellDef="let element" class="action-cell" mat-cell>
          @if (isSuperAdmin) {
            <div
              (click)="editPeriod(element)"
              class="edit-icon">
              <fa-icon
                [icon]="['far', 'pen-to-square']"
                class="blue edit-icon"
                matTooltip="Edit Payment Period"
                matTooltipClass="fc-tooltip"
                matTooltipPosition="right"></fa-icon>
            </div>
          }
        </td>
      </ng-container>
      <tr *matHeaderRowDef="displayedColumns" mat-header-row></tr>
      <tr *matRowDef="let row; columns: displayedColumns" mat-row></tr>
    </table>
    <mat-paginator [pageSizeOptions]="[5, 10, 20]"
                   aria-label="Select page of payment periods">
    </mat-paginator>
  </div>
  <mat-divider></mat-divider>

  <div class="section-content payment-history">
    <div class="title">Payment History</div>
    <div class="section-header">Total Payments to Date</div>
    <div class="payments-to-date">{{ totalPayments }}</div>
    <div class="section-header">Payments</div>
    <ul class="payment-list">
      @for (payment of payments; track payment) {
        <li>
          <div class="payment-date">
            {{ payment.createDate | date: 'shortDate' }}
          </div>
          <div class="payment-amount">
            {{ getPaymentDisplay(payment) }}
          </div>
          @if (!payment.error) {
            <div class="payment-message">
              {{ getPaymentMessage(payment) }}
            </div>
          } @else {
            <div [title]="payment.error" class="payment-message payment-error">
              {{ payment.error }}
            </div>
          }
          <div [title]="payment.getRecordedBy()" class="recorded-by">
            {{ payment.getRecordedBy() }}
          </div>
        </li>
      }
    </ul>
  </div>
  <mat-divider></mat-divider>
  @if (canPaySites) {
    <div class="section-content">
      <div class="val-adj-section-header spaced">Payment Management</div>
      <div>
        @if (!showManualPayment && !showAdjustValue) {
          <div class="payment-actions">
            <button (click)="addManualPayment(false)" class="submit-adj-value" color="primary" mat-stroked-button>
              Record External Payment
            </button>
            <button
              (click)="addManualPayment(true)"
              [disabled]="!hasStripe"
              class="submit-adj-value"
              color="primary"
              mat-stroked-button>
              Make Manual Payment
            </button>
            @if (isSuperAdmin) {
              <button
                (click)="adjustValue(null)"
                class="submit-adj-value"
                color="primary"
                mat-stroked-button>
                Adjust Payment Revenue
              </button>
            }
          </div>
        }
        @if (showAdjustValue) {
          <div>
            <form [formGroup]="valueAdjustmentForm" class="value-adj-form">
              <div class="value-adjustment-description">
                You can adjust the landowner's ERT valuation here.
                <br />
                <p>Current Payment Valuation: {{ valuation }}</p>
              </div>
              <div class="adjustment-amount">
                <mat-form-field>
                  <mat-label>Add or Subtract an Amount
                    <fa-icon
                      #tooltip="matTooltip"
                      (click)="tooltip.toggle()"
                      [icon]="['far', 'circle-info']"
                      class="blue"
                      matTooltip="The ERT adjustment, in decimal form. Enter a (-) for a negative adjustment. e.g. -12.3456"
                      matTooltipClass="fc-tooltip"
                      matTooltipPosition="right"></fa-icon>
                  </mat-label>
                  <input formControlName="amount" id="amount" matInput type="number" />
                </mat-form-field>
              </div>
            </form>
            <div class="payment-adjustment-button-container">
              <button
                (click)="cancelAdjustValue()"
                class="payment-button"
                color="primary"
                data-cy="button-cancel"
                mat-stroked-button>
                Cancel
              </button>
              <button
                (click)="submitAdjustValue()"
                [disabled]="!valueAdjustmentForm.valid"
                class="submit-adj-value"
                color="primary"
                mat-raised-button>
                Submit Payment Adjustment
              </button>
            </div>
          </div>
        }
        @if (showManualPayment) {
          <div>
            <form [formGroup]="newManualPayment">
              <div class="form-container">
                <div class="payment-field-row">
                  <div class="payment-data">
                    <fc-datepicker
                      formControlName="paymentDate"
                      formFieldClass="paymentDate-container"
                      inputID="paymentDate"
                      label="Payment Date"></fc-datepicker>
                  </div>
                  <div class="payment-data">
                    <mat-form-field>
                      <mat-label>Payment Amount {{ maxPaymentLabel }}</mat-label>
                      <input formControlName="paymentAmount" id="paymentAmount" matInput required type="number" />
                    </mat-form-field>
                  </div>
                </div>
                <div class="payment-field-row">
                  <mat-form-field class="w-100-1">
                    <mat-label>Payment Note</mat-label>
                    <textarea formControlName="paymentNote" id="paymentNote" matInput required rows="4"></textarea>
                  </mat-form-field>
                </div>
              </div>
            </form>
            <button (click)="handleNewPaymentButton(false)" class="payment-button" color="primary" mat-stroked-button>
              Cancel
            </button>
            <button
              (click)="handleNewPaymentButton(true)"
              class="payment-button"
              color="primary"
              disabled="{{ !newManualPayment.valid }}"
              mat-raised-button>
              {{ expressPayment ? 'Submit' : 'Record' }} Payment
            </button>
            <div class="error">{{ error }}</div>
          </div>
        }
      </div>
    </div>
  }

  @if (showStripeLink) {
    <div class="legal-disclaimer">
      {{ stripeDisclaimer }}
    </div>
  }
</mat-expansion-panel>
